import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, Lock, ChevronLeft, ChevronRight, Instagram, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/common/Logo";

const LEFT = [
  ["Shop", "/shop"],
  ["The Mask", "/product/leopard-luxe-led-light-therapy-mask"],
  ["Reviews", "/reviews"],
];
const RIGHT = [
  ["Our Story", "/about"],
  ["The Science", "/science"],
];
const ALL = [...LEFT, ...RIGHT, ["Candles", "/candles"]];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { count, setOpen } = useCart();
  const loc = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  useEffect(() => setMobile(false), [loc.pathname]);

  if (loc.pathname === "/cart") {
    return (
      <header data-testid="navbar-checkout" className="sticky top-0 z-50 w-full border-b border-[#F2E8E6] bg-[#FFFCFA]/90 backdrop-blur-xl">
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
          <Link to="/shop" data-testid="checkout-continue" className="inline-flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.14em] text-[#9A8E88] transition-colors hover:text-[#2C2B2C]">
            <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Continue shopping</span><span className="sm:hidden">Back</span>
          </Link>
          <Logo to="/" testid="nav-logo" className="md:absolute md:left-1/2 md:-translate-x-1/2" />
          <span className="inline-flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.14em] text-[#9A8E88]">
            <Lock className="h-3.5 w-3.5 text-[#E97C6A]" /> <span className="hidden sm:inline">Secure checkout</span>
          </span>
        </nav>
      </header>
    );
  }

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        scrolled ? "border-b border-[#F2E8E6] bg-[#FFFCFA]/85 backdrop-blur-xl" : "border-b border-transparent bg-[#FFF0E8]/60 backdrop-blur-sm"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12 md:py-5">
        <button className="md:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-open" aria-label="Menu">
          <Menu className="h-5 w-5" />
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {LEFT.map(([label, to]) => (
            <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} data-active={loc.pathname === to}
              className="nav-underline text-[0.72rem] uppercase tracking-[0.18em] text-[#6E6168] transition-colors hover:text-[#2C2B2C] data-[active=true]:text-[#2C2B2C]">
              {label}
            </Link>
          ))}
        </div>

        <Logo to="/" testid="nav-logo" className="md:absolute md:left-1/2 md:-translate-x-1/2" />

        <div className="flex items-center gap-5 md:gap-7">
          <div className="hidden items-center gap-8 md:flex">
            {RIGHT.map(([label, to]) => (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} data-active={loc.pathname === to}
                className="nav-underline text-[0.72rem] uppercase tracking-[0.18em] text-[#6E6168] transition-colors hover:text-[#2C2B2C] data-[active=true]:text-[#2C2B2C]">
                {label}
              </Link>
            ))}
          </div>
          <button onClick={() => setOpen(true)} data-testid="cart-button" className="relative" aria-label="Cart">
            <ShoppingBag className="h-5 w-5 text-[#2C2B2C]" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-2 -top-2 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[#E97C6A] px-1 text-[0.6rem] font-semibold text-white">
                {count}
              </span>
            )}
          </button>
          <Link to="/product/single" data-testid="nav-cta"
            className="hidden rounded-full bg-[#232222] px-6 py-2.5 text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-[#FFFCFA] transition-all hover:bg-[#3a3636] sm:inline-block">
            Shop the Mask
          </Link>
        </div>
      </nav>

      {/* Mobile menu (portaled to body to escape header's backdrop-blur containing block) */}
      {createPortal(
        <AnimatePresence>
          {mobile && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[130] flex flex-col bg-[#FFFCFA] md:hidden" data-testid="mobile-menu">
            <div className="flex items-center justify-between border-b border-[#F2E8E6] px-6 py-4">
              <Logo to="/" testid="mobile-nav-logo" />
              <button onClick={() => setMobile(false)} data-testid="mobile-menu-close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5DBC8] text-[#2C2B2C] transition-colors hover:bg-[#FFF0E8]">
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-6">
              <p className="px-2 pb-1 text-[0.6rem] font-semibold uppercase tracking-[0.24em] text-[#B6A99F]">Menu</p>
              {ALL.map(([label, to], idx) => (
                <motion.div key={to}
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + idx * 0.05 }}>
                  <Link to={to} data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`} data-active={loc.pathname === to}
                    className="group flex items-center justify-between border-b border-[#F5EDE7] px-2 py-4 font-display text-[1.7rem] leading-none text-[#2C2B2C] transition-colors hover:text-[#E97C6A] data-[active=true]:text-[#E97C6A]">
                    {label}
                    <ChevronRight className="h-5 w-5 text-[#D8CCC2] transition-transform group-hover:translate-x-1 group-hover:text-[#E97C6A]" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="border-t border-[#F2E8E6] px-5 pb-7 pt-4">
              <Link to="/product/single" data-testid="mobile-nav-cta"
                className="flex items-center justify-center rounded-full bg-[#232222] py-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#FFFCFA] transition-colors hover:bg-[#3a3636]">
                Shop the Mask
              </Link>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-[0.66rem] uppercase tracking-[0.12em] text-[#8A7F86]">
                <Truck className="h-3.5 w-3.5 text-[#E97C6A]" /> Free shipping · 30-day returns
              </p>
              <div className="mt-4 flex items-center justify-center gap-6 text-[0.7rem] uppercase tracking-[0.14em] text-[#8A7F86]">
                <Link to="/contact" className="transition-colors hover:text-[#2C2B2C]">Contact</Link>
                <Link to="/faq" className="transition-colors hover:text-[#2C2B2C]">FAQ</Link>
                <a href="https://www.instagram.com/auraglowwellness/" target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 transition-colors hover:text-[#2C2B2C]"><Instagram className="h-3.5 w-3.5" /> Instagram</a>
              </div>
            </div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};
